import { useState } from 'react';
import { Link, Navigate, useNavigate, useParams } from 'react-router';
import {
  BRAND,
  SUPPORT_SESSION_STATUSES,
  type AuditEventDto,
  type SupportSessionDto,
  type SupportSessionStatus,
} from '@br/protocol';
import { errorMessage } from '../api/client';
import {
  useArchiveProfile,
  useCreateSupportSession,
  useDeleteProfileData,
  useEgressIp,
  useProfile,
  useProfileAction,
  useProfileEvents,
  useRevokeSupportSession,
  useSupportSessions,
  useUpdateSupportSession,
} from '../api/queries';
import { RuntimeBadge, StatusBadge } from '../components/badges';
import { useToast } from '../components/toast';
import {
  Alert,
  Badge,
  Button,
  Card,
  ConfirmDialog,
  CopyButton,
  Dot,
  EmptyState,
  Field,
  FullPageSpinner,
  KeyValue,
  Modal,
  Select,
  type Tone,
} from '../components/ui';
import { formatDateTime, formatTime, shortUrl } from '../lib/format';
import { ACTOR_LABEL, MODE_LABEL, STATUS_LABEL, auditLabel } from '../lib/labels';
import { usePageTitle } from '../lib/title';
import { ProfileEditDialog } from './ProfileEditDialog';

function supportSessionAvailability(session: SupportSessionDto): { label: string; tone: Tone } {
  if (session.revokedAt) return { label: '폐기됨', tone: 'muted' };
  if (new Date(session.expiresAt).getTime() <= Date.now())
    return { label: '만료됨', tone: 'muted' };
  if (session.useCount >= session.maxUses) return { label: '접속 횟수 소진', tone: 'warning' };
  return { label: '접속 가능', tone: 'success' };
}

function payloadSummary(event: AuditEventDto): string {
  const payload = event.payload;
  if (!payload) return '';
  if (typeof payload.url === 'string') return payload.url;
  if (typeof payload.status === 'string')
    return STATUS_LABEL[payload.status as SupportSessionStatus] ?? payload.status;
  if (typeof payload.controllerName === 'string') return `→ ${payload.controllerName}`;
  if (payload.controllerName === null) return '→ 없음';
  if (typeof payload.reason === 'string') return payload.reason;
  if (payload.error && typeof payload.error === 'object' && 'message' in payload.error)
    return String((payload.error as { message: unknown }).message);
  if (typeof payload.bytes === 'number') return `${Math.round(payload.bytes / 1024)} KB`;
  if (typeof payload.width === 'number' && typeof payload.height === 'number')
    return `${payload.width}×${payload.height}`;
  return '';
}

function idleLabel(idleMinutes: number | null, effectiveIdleMinutes: number): string {
  if (idleMinutes === 0) return '자동 종료 안 함';
  if (idleMinutes === null)
    return effectiveIdleMinutes === 0
      ? '서버 기본값 · 자동 종료 안 함'
      : `서버 기본값 · ${effectiveIdleMinutes}분`;
  return `${idleMinutes}분`;
}

export function ProfileDetailPage() {
  const { id = '' } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const toast = useToast();
  const profileQuery = useProfile(id);
  const sessions = useSupportSessions(id);
  const events = useProfileEvents(id);
  const browserAction = useProfileAction(id);
  const archive = useArchiveProfile();
  const deleteData = useDeleteProfileData(id);
  const egress = useEgressIp(id);
  const createSession = useCreateSupportSession(id);
  const updateSession = useUpdateSupportSession(id);
  const revokeSession = useRevokeSupportSession(id);
  const [editing, setEditing] = useState(false);
  const [confirmArchive, setConfirmArchive] = useState(false);
  const [confirmDataDelete, setConfirmDataDelete] = useState(false);
  const [sessionOpen, setSessionOpen] = useState(false);
  const [sessionExpires, setSessionExpires] = useState('1440');
  const [sessionMaxUses, setSessionMaxUses] = useState('5');
  usePageTitle(profileQuery.data?.name ?? '브라우저 프로필');

  if (!id) return <Navigate to="/profiles" replace />;
  if (profileQuery.isPending) return <FullPageSpinner />;
  if (profileQuery.isError) return <Alert level="error">{errorMessage(profileQuery.error)}</Alert>;

  const profile = profileQuery.data;
  const archived = Boolean(profile.archivedAt);
  const fail = (error: unknown) => toast.error(errorMessage(error));

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2 text-xs text-ink-muted">
            <Link to="/profiles" className="hover:underline">
              브라우저 프로필
            </Link>
            <span>/</span>
            <span className="truncate">{profile.id}</span>
          </div>
          <h1 className="mt-1 flex flex-wrap items-center gap-2 text-xl font-semibold text-ink">
            {profile.name}
            {archived && <Badge tone="muted">보관됨</Badge>}
            <RuntimeBadge runtime={profile.runtime} />
          </h1>
          {profile.customerLabel && (
            <p className="text-sm text-ink-muted">고객: {profile.customerLabel}</p>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {!archived && (
            <Button onClick={() => setEditing(true)} size="sm">
              편집
            </Button>
          )}
          {!archived &&
            (profile.runtime === 'open' || profile.runtime === 'launching' ? (
              <Button
                size="sm"
                onClick={() => browserAction.mutate('close', { onError: fail })}
                loading={browserAction.isPending && browserAction.variables === 'close'}
              >
                브라우저 닫기
              </Button>
            ) : (
              <Button
                size="sm"
                onClick={() => browserAction.mutate('open', { onError: fail })}
                loading={browserAction.isPending && browserAction.variables === 'open'}
                disabled={!profile.nodeOnline}
              >
                브라우저 열기
              </Button>
            ))}
          {!archived && (
            <Button variant="primary" size="sm" onClick={() => navigate(`/profiles/${id}/view`)}>
              뷰어 열기
            </Button>
          )}
        </div>
      </div>

      {!profile.nodeOnline && (
        <Alert level="warning">
          {BRAND.nodeName} <strong>{profile.nodeName}</strong>이(가) 오프라인입니다. 노드가 다시
          연결되면 브라우저를 열 수 있습니다.
        </Alert>
      )}

      <div className="grid gap-5 lg:grid-cols-3">
        <div className="space-y-5 lg:col-span-2">
          <Card
            title="브라우저 탭"
            actions={<span className="text-xs text-ink-muted">활성 탭은 녹색 점</span>}
          >
            {profile.tabs.length === 0 ? (
              <EmptyState
                title="열린 탭이 없습니다"
                hint={
                  profile.runtime === 'open' ? undefined : '브라우저를 열면 초기 URL 탭이 생깁니다'
                }
              />
            ) : (
              <ul className="divide-y divide-line">
                {profile.tabs.map((tab) => (
                  <li key={tab.tabId} className="flex items-center gap-3 py-2 text-sm">
                    <Dot
                      online={tab.tabId === profile.activeTabId}
                      title={tab.tabId === profile.activeTabId ? '활성 탭' : '비활성 탭'}
                    />
                    <div className="min-w-0 flex-1">
                      <div className="truncate font-medium text-ink">
                        {tab.title || shortUrl(tab.url)}
                      </div>
                      <div className="truncate font-mono text-xs text-ink-muted">{tab.url}</div>
                    </div>
                    {tab.openerTabId && <Badge tone="neutral">팝업</Badge>}
                    {!archived && (
                      <Link
                        to={`/profiles/${id}/view?tab=${encodeURIComponent(tab.tabId)}`}
                        className="text-xs font-medium text-primary hover:underline"
                      >
                        보기
                      </Link>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </Card>

          <Card
            title="지원 세션"
            actions={
              !archived && (
                <Button size="sm" onClick={() => setSessionOpen(true)}>
                  새 지원 세션
                </Button>
              )
            }
            padded={false}
          >
            {sessions.isPending && <FullPageSpinner />}
            {sessions.isError && (
              <Alert level="error" className="m-4">
                {errorMessage(sessions.error)}
              </Alert>
            )}
            {sessions.data?.length === 0 && (
              <EmptyState
                title="지원 세션이 없습니다"
                hint="이 프로필을 사용하는 고객 지원 건마다 링크와 코드를 만들 수 있습니다"
              />
            )}
            {sessions.data && sessions.data.length > 0 && (
              <ul className="divide-y divide-line">
                {sessions.data.map((session) => {
                  const availability = supportSessionAvailability(session);
                  return (
                    <li key={session.id} className="space-y-2 px-4 py-3 text-sm">
                      <div className="flex flex-wrap items-center gap-2">
                        <code className="rounded bg-canvas px-2 py-0.5 font-mono text-base tracking-widest">
                          {session.code}
                        </code>
                        <StatusBadge status={session.status} />
                        <Badge tone={availability.tone}>{availability.label}</Badge>
                        <span className="text-xs text-ink-muted">
                          접속 {session.useCount}/{session.maxUses} · 만료{' '}
                          {formatDateTime(session.expiresAt)}
                        </span>
                      </div>
                      <div className="truncate font-mono text-xs text-ink-muted">{session.url}</div>
                      <div className="flex flex-wrap items-center gap-2">
                        <Select
                          aria-label="지원 세션 상태"
                          className="h-8 w-auto py-0 text-xs"
                          value={session.status}
                          disabled={
                            archived || Boolean(session.revokedAt) || updateSession.isPending
                          }
                          onChange={(event) =>
                            updateSession.mutate(
                              {
                                supportSessionId: session.id,
                                status: event.target.value as SupportSessionStatus,
                              },
                              { onError: fail },
                            )
                          }
                        >
                          {SUPPORT_SESSION_STATUSES.map((status) => (
                            <option key={status} value={status}>
                              {STATUS_LABEL[status]}
                            </option>
                          ))}
                        </Select>
                        <CopyButton text={session.code} label="코드 복사" />
                        <CopyButton text={session.url} label="링크 복사" />
                        {!archived && !session.revokedAt && (
                          <Link
                            to={`/profiles/${id}/view?supportSessionId=${encodeURIComponent(session.id)}`}
                            className="inline-flex h-8 items-center rounded-md px-3 text-sm font-medium text-primary hover:bg-primary-soft"
                          >
                            이 세션으로 뷰어 열기
                          </Link>
                        )}
                        {!archived && !session.revokedAt && (
                          <Button
                            size="sm"
                            variant="danger"
                            className="ml-auto"
                            onClick={() => revokeSession.mutate(session.id, { onError: fail })}
                          >
                            폐기
                          </Button>
                        )}
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </Card>

          <Card title="이벤트 로그" padded={false}>
            {events.isPending && <FullPageSpinner />}
            {events.isError && (
              <Alert level="error" className="m-4">
                {errorMessage(events.error)}
              </Alert>
            )}
            {events.data && (
              <>
                {events.data.pages.flat().length === 0 && <EmptyState title="기록이 없습니다" />}
                <ul className="divide-y divide-line">
                  {events.data.pages.flat().map((event) => (
                    <li key={event.id} className="flex items-start gap-3 px-4 py-2 text-sm">
                      <span
                        className="w-20 shrink-0 font-mono text-xs text-ink-muted"
                        title={event.at}
                      >
                        {formatTime(event.at)}
                      </span>
                      <span className="w-14 shrink-0 text-xs text-ink-muted">
                        {ACTOR_LABEL[event.actorType] ?? event.actorType}
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="font-medium text-ink">{auditLabel(event.type)}</span>
                        {event.actorName && (
                          <span className="ml-1 text-ink-muted">· {event.actorName}</span>
                        )}
                        {payloadSummary(event) && (
                          <span className="ml-2 break-all text-xs text-ink-muted">
                            {payloadSummary(event)}
                          </span>
                        )}
                      </span>
                    </li>
                  ))}
                </ul>
                {events.hasNextPage && (
                  <div className="border-t border-line p-3 text-center">
                    <Button
                      size="sm"
                      onClick={() => events.fetchNextPage()}
                      loading={events.isFetchingNextPage}
                    >
                      더 보기
                    </Button>
                  </div>
                )}
              </>
            )}
          </Card>
        </div>

        <div className="space-y-5">
          <Card title="프로필 정보">
            <KeyValue
              items={[
                [
                  '노드',
                  <span key="node" className="inline-flex items-center gap-1.5">
                    <Dot online={profile.nodeOnline} />
                    {profile.nodeName}
                  </span>,
                ],
                [
                  '참여자',
                  `${profile.participantCount}명${profile.controllerName ? ` · 제어: ${profile.controllerName}` : ''}`,
                ],
                ['창 크기', `${profile.size.width}×${profile.size.height}`],
                ['전송 모드', MODE_LABEL[profile.defaultMode]],
                ['자동 종료', idleLabel(profile.idleMinutes, profile.effectiveIdleMinutes)],
                [
                  'headless',
                  profile.headless === null ? '노드 설정 따름' : profile.headless ? '켬' : '끔',
                ],
                [
                  '브라우저 지역',
                  profile.locale || profile.timezoneId
                    ? `${profile.locale ?? '노드 설정'} · ${profile.timezoneId ?? '노드 설정'}`
                    : '노드 설정 따름',
                ],
                [
                  '프록시',
                  profile.proxy ? (
                    <span key="proxy">
                      {profile.proxy.server}
                      {profile.proxy.username && (
                        <span className="text-ink-muted"> · {profile.proxy.username}</span>
                      )}
                      {profile.proxy.hasPassword && (
                        <span className="text-ink-muted"> · 비밀번호 저장됨</span>
                      )}
                      {profile.proxy.bypass && (
                        <span className="block text-xs text-ink-muted">
                          우회: {profile.proxy.bypass}
                        </span>
                      )}
                    </span>
                  ) : (
                    '없음'
                  ),
                ],
                ['생성자', profile.createdByName],
                ['생성', formatDateTime(profile.createdAt)],
                ['마지막 열림', formatDateTime(profile.lastOpenedAt)],
              ]}
            />
            <p className="mt-3 border-t border-line pt-3 text-xs text-ink-muted">
              자동 종료 시간은 모든 뷰어가 나간 뒤부터 계산됩니다. Chrome 프로세스만 닫고 프로필
              데이터는 삭제하지 않습니다. 단, 세션 쿠키만 사용하는 사이트는 Chrome 재시작 시 다시
              로그인이 필요할 수 있습니다.
            </p>
          </Card>

          <Card title="유틸리티">
            <div className="space-y-3 text-sm">
              <div>
                <Button
                  size="sm"
                  onClick={() => egress.mutate(undefined, { onError: fail })}
                  loading={egress.isPending}
                  disabled={profile.runtime !== 'open'}
                >
                  현재 egress IP 조회
                </Button>
                {egress.data && (
                  <p className="mt-2 font-mono text-xs">
                    {egress.data.ip ?? (
                      <span className="text-danger">{egress.data.error ?? '조회 실패'}</span>
                    )}
                  </p>
                )}
                {profile.runtime !== 'open' && (
                  <p className="mt-1 text-xs text-ink-muted">
                    브라우저가 열려 있을 때만 조회할 수 있습니다.
                  </p>
                )}
              </div>
              {!archived && (
                <div className="border-t border-line pt-3">
                  <Button
                    size="sm"
                    variant="danger"
                    disabled={!profile.nodeOnline}
                    onClick={() => setConfirmDataDelete(true)}
                  >
                    브라우저 데이터 삭제
                  </Button>
                  <p className="mt-1 text-xs text-ink-muted">
                    {profile.nodeOnline
                      ? '이 프로필의 쿠키와 로그인 상태를 지웁니다. 프로필과 지원 세션 자체는 남습니다.'
                      : '노드가 온라인일 때만 브라우저 데이터를 삭제할 수 있습니다.'}
                  </p>
                </div>
              )}
              {!archived && (
                <div className="border-t border-line pt-3">
                  <Button size="sm" variant="danger" onClick={() => setConfirmArchive(true)}>
                    프로필 보관
                  </Button>
                  <p className="mt-1 text-xs text-ink-muted">
                    브라우저를 닫고 모든 지원 세션과 고객 접속을 종료합니다. 브라우저 데이터는
                    보존됩니다.
                  </p>
                </div>
              )}
            </div>
          </Card>
        </div>
      </div>

      {editing && <ProfileEditDialog open profile={profile} onClose={() => setEditing(false)} />}

      <Modal
        open={sessionOpen}
        title="새 지원 세션"
        onClose={() => setSessionOpen(false)}
        size="sm"
        footer={
          <>
            <Button onClick={() => setSessionOpen(false)}>취소</Button>
            <Button
              variant="primary"
              loading={createSession.isPending}
              onClick={() =>
                createSession.mutate(
                  {
                    expiresInMinutes: Number(sessionExpires),
                    maxUses: Number(sessionMaxUses),
                  },
                  {
                    onSuccess: () => setSessionOpen(false),
                    onError: fail,
                  },
                )
              }
            >
              만들기
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          <p className="text-sm text-ink-muted">
            고객 지원 건마다 별도 세션을 만들 수 있습니다. 모든 세션은 같은 브라우저 프로필과 로그인
            상태를 사용합니다.
          </p>
          <Field label="유효 기간">
            <Select
              value={sessionExpires}
              onChange={(event) => setSessionExpires(event.target.value)}
            >
              <option value="60">1시간</option>
              <option value="1440">1일</option>
              <option value="10080">7일</option>
              <option value="43200">30일</option>
            </Select>
          </Field>
          <Field label="최대 접속 횟수" hint="새 브라우저에서 링크나 코드를 등록하는 횟수입니다">
            <Select
              value={sessionMaxUses}
              onChange={(event) => setSessionMaxUses(event.target.value)}
            >
              <option value="1">1회</option>
              <option value="5">5회</option>
              <option value="20">20회</option>
              <option value="100">100회</option>
            </Select>
          </Field>
        </div>
      </Modal>

      <ConfirmDialog
        open={confirmDataDelete}
        title="브라우저 데이터를 삭제할까요?"
        message="Chrome을 닫고 저장된 로그인 상태, 쿠키, 캐시를 모두 지웁니다. 프로필과 지원 세션은 유지되지만 이 작업은 되돌릴 수 없습니다."
        confirmLabel="데이터 삭제"
        danger
        loading={deleteData.isPending}
        onCancel={() => setConfirmDataDelete(false)}
        onConfirm={() =>
          deleteData.mutate(undefined, {
            onSuccess: () => {
              setConfirmDataDelete(false);
              toast.success('브라우저 데이터를 삭제했습니다');
            },
            onError: fail,
          })
        }
      />
      <ConfirmDialog
        open={confirmArchive}
        title="브라우저 프로필을 보관할까요?"
        message="브라우저를 닫고 모든 지원 세션을 완료·폐기하며 고객 접속을 종료합니다. 저장된 브라우저 데이터는 유지됩니다."
        confirmLabel="보관"
        danger
        loading={archive.isPending}
        onCancel={() => setConfirmArchive(false)}
        onConfirm={() =>
          archive.mutate(id, {
            onSuccess: () => {
              setConfirmArchive(false);
              toast.success('브라우저 프로필을 보관했습니다');
            },
            onError: fail,
          })
        }
      />
    </div>
  );
}
